/**
 * AuthContext — wraps MSAL authentication state for the app.
 *
 * Initialises MSAL on mount, handles redirect callback, and exposes
 * user info + login/logout to the component tree.
 *
 * If Entra ID is not configured (no VITE_AZURE_CLIENT_ID),
 * falls through to a dev-mode stub so the app still works locally.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  initMsal,
  getAccount,
  login as msalLogin,
  logout as msalLogout,
  isAuthenticated as checkAuth,
} from '../services/auth';
import { getMe } from '../services/api';
import { disconnect as wsDisconnect } from '../services/websocket';

const AuthContext = createContext(null);

const DEV_USER = {
  userId: 'dev-user',
  email: 'dev@localhost',
  name: 'Developer',
  roles: [],
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const azureClientId =
    (typeof window !== 'undefined' ? window._env_?.VITE_AZURE_CLIENT_ID : undefined) ||
    import.meta.env.VITE_AZURE_CLIENT_ID;
  const isDevMode = !azureClientId;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (isDevMode) {
        // No Entra ID configured — use dev stub
        setUser(DEV_USER);
        setLoading(false);
        return;
      }

      try {
        await initMsal();
        const account = getAccount();
        if (account) {
          // Fetch full profile from Backend using the Entra ID token
          const me = await getMe();
          if (!cancelled) {
            setUser(me);
          }
        }
      } catch (err) {
        console.error('[auth] Init failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      wsDisconnect();
    };
  }, [isDevMode]);

  const login = useCallback(async () => {
    if (isDevMode) {
      setUser(DEV_USER);
      return;
    }
    await msalLogin();
  }, [isDevMode]);

  const logout = useCallback(async () => {
    wsDisconnect();
    if (isDevMode) {
      setUser(null);
      return;
    }
    await msalLogout();
  }, [isDevMode]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isDevMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
